import { Routes } from '@angular/router';

import { requireAnyCapability, requireCapability } from '../core/admin-guard';

/**
 * The signed-in shell's route subtree.
 *
 * The Shell hosts ion-tabs; each tab is a thin view wrapper (in tabs.ts) that
 * owns its `ion-content` and embeds the page component directly. The wrappers
 * live in ion-tabs' own outlet, so Ionic's tab machinery keeps exactly one tab
 * in the foreground and retains each tab's scroll.
 *
 * The tab ROOTS on the bar (Dashboards, Database) plus the two detail routes
 * (partners/:id, accounts/:id) and the off-bar pages (Activity log, Settings
 * and My account, reached from the tab bar's More sheet). The partners and
 * accounts records live under the Database tab as its "Partners" and
 * "Accounts" sub-tabs, deep-linkable via /database/:section; the Dashboards
 * tab's own sub-tabs (Overview, Engagement, Revenue) are
 * deep-linkable via /dashboards/:section (see the config comment below).
 * The detail routes are FLAT - direct children of ion-tabs' outlet, at the
 * SAME level as the tab roots, NOT nested under them. That is deliberate:
 * ion-tabs derives a view's stackId from the FIRST URL segment. Neither
 * partners/:id nor accounts/:id has a matching list route, so each stands
 * alone in its own stack ('partners' / 'accounts') - arriving from Database
 * is a tab-switch, and each detail's own back control / history returns there. A nested ion-router-outlet breaks foregrounding and throws
 * NG0950, so each detail is its own ion-content-owning wrapper (in tabs.ts)
 * that reads the ':id' param via component input binding.
 *
 * The Shell component itself is supplied by app.routes.ts as the parent
 * `component`; this array is its `children`.
 *
 * Chrome is declared per route, and the BOTTOM bar and the SIDE rail are
 * separate questions:
 *
 * - `data.tabBar` - the shell's floating bottom bar. False where the page
 *   owns the bottom edge with an action bar of its own (the details) or
 *   where a footer is the only way out (the wizards).
 * - `data.sideNav` - the wide-screen side rail. Defaults to TRUE, including
 *   on the detail pages: a left column does not fight a page that merely
 *   owns the bottom, and losing your navigation every time you open a record
 *   is worse than keeping it. It is false ONLY where leaving the page early
 *   would abandon work in progress - the two wizards. Their footers are
 *   deliberately the only exit, and a rail full of escape hatches would
 *   undo that.
 *
 * A page whose `tabBar` is false but `sideNav` is true also shifts its
 * content right, and any start-aligned bar it owns clears the rail (see the
 * shell's has-rail styles) - otherwise the two would stack in the same
 * bottom-left corner.
 *
 * Each route declares its bottom chrome via `data.tabBar`: the tab ROOTS
 * carry `tabBar: true` (show the shell's shared tab bar); the DETAIL routes
 * carry `tabBar: false` (the shared bar slides out - the page shows its own
 * action bar, or none). The Shell reads the deepest active route's `data` to
 * decide, so visibility is declarative per route (Ionic's children-vs-siblings
 * idea, expressed in route config) without giving up the flat-child routing
 * that makes push/pop and swipe-back work.
 */
export const shellChildren: Routes = [
  // The dashboards' sub-tabs are deep-linkable: /dashboards/:section maps to
  // the panes (overview | engagement | revenue). ONE component-bearing
  // config: param-only navigations REUSE the DashboardsTab instance (no Ionic
  // page push, no remount), so the kit strip's veil motion and per-tab
  // scroll ledger carry across section changes. Plain /dashboards redirects
  // to the first section; Ionic's tab memory restores the last-visited
  // section URL when returning via the bottom bar. stackId still derives
  // from the FIRST segment, so every section stays in the 'dashboards' stack.
  { path: 'dashboards', redirectTo: 'dashboards/overview', pathMatch: 'full' },
  {
    path: 'dashboards/:section',
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.DashboardsTab),
  },
  // The Database page's sections follow the same deep-link recipe:
  // /database/:section (partners | accounts | activity).
  { path: 'database', redirectTo: 'database/partners', pathMatch: 'full' },
  {
    path: 'database/:section',
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.DatabaseTab),
  },
  // Adding a partner is its own full-page wizard under the Database tab's
  // partners section (/database/partners/add). Three segments, so it never
  // collides with the two-segment database/:section above; it shares the
  // 'database' stackId, so it PUSHES over the partners table and swipe-back /
  // its own Cancel return there. tabBar: false - the wizard's footer owns the
  // way out, and it carries no page header at all (see PartnerAddTab).
  // The calendar grew from a dashboards sub-tab into a tab of its own; links
  // minted in that era still point at the old address, and normalising them to
  // Overview (the unknown-slug fallback) would read as the calendar vanishing.
  { path: 'dashboards/calendar', redirectTo: 'calendar' },
  // The Calendar tab. A primary stack, sibling of Dashboards and Database,
  // with their exact deep-link recipe: /calendar/:section maps to the layout
  // panes (list | compact | grid), one component-bearing config so a
  // section change reuses the page instance.
  { path: 'calendar', redirectTo: 'calendar/list', pathMatch: 'full' },
  {
    path: 'calendar/:section',
    canActivate: [requireCapability('nav.calendar')],
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.CalendarTab),
  },
  // The Support tab - the platform's support inbox (filed from the user
  // app's Help Center and data opt-in surfaces) and its analytics. Same
  // deep-link recipe as the other primary tabs: /support/:section
  // (overview | new | in-progress | resolved), one component-bearing config
  // so a section change reuses the page instance. The slug matches the
  // lifecycle status it shows; an unknown slug normalises to Overview.
  { path: 'support', redirectTo: 'support/overview', pathMatch: 'full' },
  {
    path: 'support/:section',
    canActivate: [requireCapability('support.read')],
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.SupportTab),
  },
  {
    path: 'database/partners/add',
    canActivate: [requireCapability('partners.write')],
    // Both off: the wizard's footer owns the way out, and a rail would offer
    // exits that abandon a half-filled form.
    data: { tabBar: false, sideNav: false },
    loadComponent: () => import('./tabs').then((m) => m.PartnerAddTab),
  },
  // The partner book is the Database's "Partners" sub-tab (no route of its
  // own). Partner detail stays a flat route; arriving from Database is a
  // tab-switch, and its back / swipe returns there. Its sections follow the
  // deep-link recipe: /partners/:id/:section (overview | contacts | licenses
  // | users), one component-bearing config so section changes reuse the page.
  { path: 'partners/:id', redirectTo: 'partners/:id/overview', pathMatch: 'full' },
  {
    path: 'partners/:id/:section',
    canActivate: [requireCapability('partners.read')],
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.PartnerDetailTab),
  },
  // The accounts table is the Database's "Accounts" sub-tab (no route of
  // its own). Account detail stays a flat route; back returns to the
  // accounts table. The ':id' is the account's lowercased-email doc id.
  // Same deep-link recipe as the partner now that it has sub-tabs:
  // /accounts/:id/:section (overview | activity), one component-bearing config
  // so section changes reuse the page rather than remounting it.
  { path: 'accounts/:id', redirectTo: 'accounts/:id/overview', pathMatch: 'full' },
  {
    path: 'accounts/:id/:section',
    canActivate: [requireCapability('accounts.read')],
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.AccountDetailTab),
  },
  // Not on the bar: the More-sheet pages (Settings, My account). tabBar: false
  // because no bar item matches them - the bar slides out and the page's back
  // control brings you home.
  //
  // There is deliberately no /activity route. The log lives at
  // database/activity and nowhere else: two entry points to one surface is how
  // the Database tab ended up showing a placeholder while a separate page
  // showed the real thing.
  // Settings rides WITH the main chrome now (a sibling of the tab roots,
  // reached from the side menu's foot or the More sheet): tabBar true keeps
  // the floating bar / side rail on screen while it is open.
  {
    path: 'settings',
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.SettingsTab),
  },
  // The welcome tour. Reached two ways: the onboarding guard reroutes an admin
  // who has not been through it (carrying their destination in `?next=`), and
  // the Settings page links here to replay it. tabBar: false - the wizard's
  // footer is the only way out, and both of its buttons mark it finished.
  {
    path: 'onboarding',
    // Both off - the tour HOLDS until it is finished, so it must not render
    // navigation that walks out of it.
    data: { tabBar: false, sideNav: false },
    loadComponent: () => import('./tabs').then((m) => m.OnboardingTab),
  },
  // Seed data - reached from More like the pages above. It carries TWO
  // unrelated tools: the mock-data seeder and the launch import. The More
  // entry and the palette action use the same either-or test; the Callables
  // are the real authority.
  {
    path: 'seed-data',
    // NOT behind SEED_DATA_AVAILABLE any more. That flag hides DEMO tooling in
    // production, correctly - but the launch import lives on the same page and
    // was inheriting the gate, so the tool for importing the launch cohort was
    // unreachable in the one environment where the cohort matters. The page's
    // two halves now gate themselves: mock data on mockdata.write, the launch
    // import on launch.populate, and the whole route on holding either.
    canActivate: [requireAnyCapability('mockdata.write', 'launch.populate')],
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.SeedDataTab),
  },
  // Import domains - off the bar, reached from the command palette. Same
  // capability as editing domains on one partner; the Callable re-checks it.
  {
    path: 'import-domains',
    canActivate: [requireCapability('partners.write')],
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.DomainImportTab),
  },
  // The shell's landing route. The redirect MUST be absolute: this is an
  // empty-path child of an empty-path parent (app.routes.ts), so a relative
  // 'dashboards' has no segments to anchor against - it resolves to the
  // parent's own empty path and silently collapses, activating the shell with
  // an empty outlet. The other redirects here are relative and fine: they all
  // sit on a non-empty path. Hop only as far as /dashboards so the default
  // section stays declared in ONE place (above).
  { path: '', redirectTo: '/dashboards', pathMatch: 'full' },
];
